import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Line, Bar } from "react-chartjs-2";
import {
  Box,
  Button,
  Divider,
  Heading,
  Input,
  Radio,
  RadioGroup,
  Select,
  Skeleton,
  Stack,
  Text,
  useColorModeValue,
  VStack,
  SimpleGrid,
  HStack,
  Badge,
  useToast,
  Spinner,
} from "@chakra-ui/react";
import { Trans } from "@lingui/react/macro";
import { t } from "@lingui/core/macro";
import Navbar from "../components/Navbar";
import useAxios from "../utils/useAxios";
import { IDevice } from "../types/deviceInterface";

// ─── Interfaces ─────────────────────────────────────────────────────────────

interface IFieldSummary {
  id: number;
  field_name: string;
}

interface IPlottableDeviceData {
  [key: string]: Array<number | null> | string[] | undefined;
  timestamp: string[];
  formatted_timestamp?: string[];
}

interface IFieldWeatherHistoryPoint {
  timestamp: string;
  time?: string;
  [key: string]: number | string | null | undefined;
}

interface IFieldWeatherHistoryResponse {
  series?: IFieldWeatherHistoryPoint[];
  results?: IFieldWeatherHistoryPoint[];
  hourly?: {
    time: string[];
    [key: string]: Array<number | string | null>;
  };
  daily?: {
    time: string[];
    [key: string]: Array<number | string | null>;
  };
}

type SourceType = "sensor" | "field" | "";

// Sensor parameter keys returned by the plottable-data endpoint
const SENSOR_PARAM_LABELS: Record<string, string> = {
  soil_moisture: "Soil Moisture",
  soil_moisture_2: "Soil Moisture 2",
  soil_moisture_3: "Soil Moisture 3",
  moisture_index_1: "Moisture Index 1",
  moisture_index_2: "Moisture Index 2",
  moisture_index_3: "Moisture Index 3",
  soil_temperature: "Soil Temperature",
  soil_conductivity: "Soil Conductivity",
  soil_conductivity_2: "Soil Conductivity 2",
  soil_conductivity_3: "Soil Conductivity 3",
  air_temperature: "Air Temperature",
  humidity: "Humidity",
  lux: "Lux",
  battery_voltage: "Battery Voltage",
  battery: "Battery",
};

// Field weather parameter keys from /agtech/fields/{field_id}/weather/history
const FIELD_HOURLY_PARAM_LABELS: Record<string, string> = {
  temperature: "Temperature",
  temp_min: "Temp Min",
  temp_max: "Temp Max",
  feels_like: "Feels Like",
  humidity: "Humidity",
  precipitation: "Precipitation",
  pressure: "Pressure",
  clouds: "Clouds",
  wind_speed: "Wind Speed",
  wind_deg: "Wind Direction",
};

// Weather param keys for daily field data
const FIELD_DAILY_PARAM_LABELS: Record<string, string> = {
  temperature: "Temperature",
  temp_min: "Temp Min",
  temp_max: "Temp Max",
  precipitation: "Precipitation",
  wind_speed: "Wind Speed",
};

// ─── ScaleConfigurator ───────────────────────────────────────────────────────

interface ScaleState {
  sourceType: SourceType;
  deviceId: string;
  deviceParam: string;
  fieldId: string;
  weatherParam: string;
}

interface ScaleConfiguratorProps {
  label: string;
  accentColor: string;
  state: ScaleState;
  onChange: (next: Partial<ScaleState>) => void;
  devices: IDevice[];
  fields: IFieldSummary[];
  resolution: "hourly" | "daily";
  availableDeviceParams: string[];
  listsLoading: boolean;
}

const ScaleConfigurator = ({
  label,
  accentColor,
  state,
  onChange,
  devices,
  fields,
  resolution,
  availableDeviceParams,
  listsLoading,
}: ScaleConfiguratorProps) => {
  const fieldWeatherLabels =
    resolution === "hourly" ? FIELD_HOURLY_PARAM_LABELS : FIELD_DAILY_PARAM_LABELS;

  return (
    <Box borderLeft="4px solid" borderColor={accentColor} pl={4} py={2}>
      <HStack mb={3}>
        <Badge colorScheme={accentColor === "#48BB78" ? "green" : "blue"} fontSize="sm">
          {label}
        </Badge>
      </HStack>

      {/* Step 1: Source type */}
      <Text fontWeight="semibold" mb={1} fontSize="sm">
        <Trans>Data source</Trans>
      </Text>
      <RadioGroup
        value={state.sourceType}
        onChange={(v) =>
          onChange({ sourceType: v as SourceType, deviceParam: "", weatherParam: "" })
        }
        mb={3}
      >
        <Stack direction="row">
          <Radio value="sensor">
            <Trans>Sensor</Trans>
          </Radio>
          <Radio value="field">
            <Trans>Field weather</Trans>
          </Radio>
        </Stack>
      </RadioGroup>

      {/* Step 2a: Sensor device + param */}
      {state.sourceType === "sensor" && (
        <>
          <Text fontWeight="semibold" mb={1} fontSize="sm">
            <Trans>Device</Trans>
          </Text>
          {listsLoading ? (
            <HStack mb={3}>
              <Spinner size="sm" />
              <Text fontSize="sm">
                <Trans>Loading devices…</Trans>
              </Text>
            </HStack>
          ) : (
            <Select
              mb={3}
              placeholder={t`Select device`}
              value={state.deviceId}
              onChange={(e) => onChange({ deviceId: e.target.value, deviceParam: "" })}
            >
              {devices.map((d) => (
                <option key={d.device_id} value={String(d.device_id)}>
                  {d.device_name || `Device #${d.device_id}`}
                </option>
              ))}
            </Select>
          )}

          {state.deviceId && (
            <>
              <Text fontWeight="semibold" mb={1} fontSize="sm">
                <Trans>Parameter</Trans>
              </Text>
              <Select
                mb={3}
                placeholder={t`Select parameter`}
                value={state.deviceParam}
                onChange={(e) => onChange({ deviceParam: e.target.value })}
              >
                {availableDeviceParams.map((key) => (
                  <option key={key} value={key}>
                    {SENSOR_PARAM_LABELS[key] ?? key.replace(/_/g, " ")}
                  </option>
                ))}
              </Select>
            </>
          )}
        </>
      )}

      {/* Step 2b: Field + weather param */}
      {state.sourceType === "field" && (
        <>
          <Text fontWeight="semibold" mb={1} fontSize="sm">
            <Trans>Field</Trans>
          </Text>
          {listsLoading ? (
            <HStack mb={3}>
              <Spinner size="sm" />
              <Text fontSize="sm">
                <Trans>Loading fields…</Trans>
              </Text>
            </HStack>
          ) : (
            <Select
              mb={3}
              placeholder={t`Select field`}
              value={state.fieldId}
              onChange={(e) => onChange({ fieldId: e.target.value, weatherParam: "" })}
            >
              {fields.map((f) => (
                <option key={f.id} value={String(f.id)}>
                  {f.field_name || `Field #${f.id}`}
                </option>
              ))}
            </Select>
          )}

          {state.fieldId && (
            <>
              <Text fontWeight="semibold" mb={1} fontSize="sm">
                <Trans>Weather parameter</Trans>
              </Text>
              <Select
                mb={3}
                placeholder={t`Select parameter`}
                value={state.weatherParam}
                onChange={(e) => onChange({ weatherParam: e.target.value })}
              >
                {Object.entries(fieldWeatherLabels).map(([key, lbl]) => (
                  <option key={key} value={key}>
                    {lbl}
                  </option>
                ))}
              </Select>
            </>
          )}
        </>
      )}
    </Box>
  );
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function isScaleReady(s: ScaleState): boolean {
  if (s.sourceType === "sensor") return !!s.deviceId && !!s.deviceParam;
  if (s.sourceType === "field") return !!s.fieldId && !!s.weatherParam;
  return false;
}

const dateOnlyFormat: Intl.DateTimeFormatOptions = {
  year: "2-digit",
  month: "2-digit",
  day: "2-digit",
};
const hourlyFormat: Intl.DateTimeFormatOptions = {
  year: "2-digit",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
};

function formatTimestamps(timestamps: string[], resolution: "hourly" | "daily"): string[] {
  const fmt = resolution === "hourly" ? hourlyFormat : dateOnlyFormat;
  return timestamps.map((ts) => new Date(ts).toLocaleDateString("nb-NO", fmt));
}

function toStartTimestamp(dateStr: string): string {
  return `${dateStr}T00:00:00.000Z`;
}

function toEndTimestamp(dateStr: string): string {
  return `${dateStr}T23:59:59.999Z`;
}

function toApiStartTimestamp(dateStr: string): string {
  return toStartTimestamp(dateStr);
}

function toApiEndTimestamp(dateStr: string): string {
  return toEndTimestamp(dateStr);
}

function filterSeriesByDateRange(
  timestamps: string[],
  values: (number | null)[],
  startDate: string,
  endDate: string,
): { timestamps: string[]; values: (number | null)[] } {
  const startMs = new Date(toStartTimestamp(startDate)).getTime();
  const endMs = new Date(toEndTimestamp(endDate)).getTime();
  const outTimestamps: string[] = [];
  const outValues: (number | null)[] = [];
  let parsedCount = 0;

  for (let i = 0; i < timestamps.length; i += 1) {
    const ts = timestamps[i];
    const ms = new Date(ts).getTime();
    if (!Number.isFinite(ms)) continue;
    parsedCount += 1;
    if (ms >= startMs && ms <= endMs) {
      outTimestamps.push(ts);
      outValues.push(values[i] ?? null);
    }
  }

  // If timestamp parsing fails for all points, do not blank the chart.
  if (timestamps.length > 0 && parsedCount === 0) {
    return { timestamps, values };
  }

  // If everything got filtered out but source had values, keep the source data.
  const hasSourceValues = values.some((v) => v !== null);
  if (outTimestamps.length === 0 && hasSourceValues) {
    return { timestamps, values };
  }

  return { timestamps: outTimestamps, values: outValues };
}

function alignSeriesToLabels(
  labels: string[],
  timestamps?: string[],
  values?: (number | null)[],
  resolution: "hourly" | "daily" = "hourly",
): (number | null)[] {
  if (!Array.isArray(labels) || labels.length === 0) return [];
  if (!Array.isArray(timestamps) || timestamps.length === 0) {
    return labels.map(() => null);
  }

  const normalizeKey = (ts: string): string => {
    if (resolution === "daily") {
      return ts.length >= 10 ? ts.slice(0, 10) : ts;
    }
    return ts;
  };

  const safeValues = Array.isArray(values) ? values : [];
  const map = new Map<string, number | null>();
  for (let i = 0; i < timestamps.length; i += 1) {
    map.set(normalizeKey(timestamps[i]), safeValues[i] ?? null);
  }

  const aligned = labels.map((ts) => map.get(normalizeKey(ts)) ?? null);
  if (aligned.some((v) => v !== null)) return aligned;
  return safeValues;
}

function getPointTimestamp(point: IFieldWeatherHistoryPoint): string {
  if (typeof point.timestamp === "string" && point.timestamp.length > 0) return point.timestamp;
  if (typeof point.time === "string" && point.time.length > 0) return point.time;
  return "";
}

function normalizeNumericValue(v: unknown): number | null {
  if (v === null || v === undefined) return null;
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  if (typeof v === "string") {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function getSensorTimestamps(raw: IPlottableDeviceData): string[] {
  if (Array.isArray(raw.timestamp)) return raw.timestamp;
  if (Array.isArray(raw.formatted_timestamp)) return raw.formatted_timestamp;
  return [];
}

function toDateInputValue(d: Date): string {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function dateDaysAgo(daysAgo: number): string {
  const d = new Date();
  d.setDate(d.getDate() - daysAgo);
  return toDateInputValue(d);
}

// ─── CompareView ─────────────────────────────────────────────────────────────

const DAYS_OPTIONS = [7, 14, 30, 90, 365];

const defaultScaleState = (): ScaleState => ({
  sourceType: "",
  deviceId: "",
  deviceParam: "",
  fieldId: "",
  weatherParam: "",
});

function scaleToParams(s: ScaleState, prefix: string): Record<string, string> {
  const p: Record<string, string> = {};
  if (s.sourceType) p[`${prefix}_type`] = s.sourceType;
  if (s.deviceId) p[`${prefix}_device`] = s.deviceId;
  if (s.deviceParam) p[`${prefix}_param`] = s.deviceParam;
  if (s.fieldId) p[`${prefix}_field`] = s.fieldId;
  if (s.weatherParam) p[`${prefix}_wparam`] = s.weatherParam;
  return p;
}

function paramsToScale(params: URLSearchParams, prefix: string): ScaleState {
  return {
    sourceType: (params.get(`${prefix}_type`) as SourceType) || "",
    deviceId: params.get(`${prefix}_device`) || "",
    deviceParam: params.get(`${prefix}_param`) || "",
    fieldId: params.get(`${prefix}_field`) || "",
    weatherParam: params.get(`${prefix}_wparam`) || "",
  };
}

// Parameters that should render as bar charts
const BAR_PARAMS = new Set(["precipitation", "rain"]);

function paramIsBar(scale: ScaleState): boolean {
  const key = scale.sourceType === "sensor" ? scale.deviceParam : scale.weatherParam;
  return BAR_PARAMS.has(key);
}

function getScaleParamKey(scale: ScaleState): string {
  return scale.sourceType === "sensor" ? scale.deviceParam : scale.weatherParam;
}

function getScaleFetchKey(
  scale: ScaleState,
  options: {
    timeMode: "relative" | "custom";
    days: number;
    startDate: string;
    endDate: string;
    resolution: "hourly" | "daily";
  },
): string {
  const base = [
    scale.sourceType,
    scale.deviceId,
    scale.deviceParam,
    scale.fieldId,
    scale.weatherParam,
    options.resolution,
    options.timeMode,
  ];
  const range =
    options.timeMode === "custom" ? [options.startDate, options.endDate] : [String(options.days)];
  return [...base, ...range].join("|");
}

function getCanonicalParamFamily(paramKey: string): string {
  if (paramKey.startsWith("soil_moisture")) return "soil_moisture";
  if (paramKey.startsWith("moisture_index_")) return "moisture_index";
  if (
    [
      "air_temperature",
      "soil_temperature",
      "temperature",
      "temp_min",
      "temp_max",
      "feels_like",
    ].includes(paramKey)
  ) {
    return "temperature";
  }
  if (paramKey === "humidity") return "humidity";
  return paramKey;
}

function getFamilyDisplayLabel(family: string): string {
  switch (family) {
    case "temperature":
      return "Temperature";
    case "soil_moisture":
      return "Soil Moisture";
    case "moisture_index":
      return "Moisture Index";
    case "humidity":
      return "Humidity";
    default:
      return family.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  }
}

interface ScaleData {
  timestamps: string[];
  values: (number | null)[];
  label: string;
  isBar: boolean;
  canonicalFamily: string;
}

function extractHistorySeries(
  payload: IFieldWeatherHistoryResponse | IFieldWeatherHistoryPoint[],
  resolution: "hourly" | "daily",
  weatherParam: string,
): { timestamps: string[]; values: (number | null)[] } {
  if (Array.isArray(payload)) {
    const pairs = payload
      .map((p) => {
        const ts = getPointTimestamp(p);
        const v = p[weatherParam];
        return {
          ts,
          value: normalizeNumericValue(v),
        };
      })
      .filter((x) => x.ts.length > 0);

    return {
      timestamps: pairs.map((x) => x.ts),
      values: pairs.map((x) => x.value),
    };
  }

  if (Array.isArray(payload.series)) {
    const pairs = payload.series
      .map((p) => {
        const ts = getPointTimestamp(p);
        const v = p[weatherParam];
        return {
          ts,
          value: normalizeNumericValue(v),
        };
      })
      .filter((x) => x.ts.length > 0);

    return {
      timestamps: pairs.map((x) => x.ts),
      values: pairs.map((x) => x.value),
    };
  }

  if (Array.isArray(payload.results)) {
    const pairs = payload.results
      .map((p) => {
        const ts = getPointTimestamp(p);
        const v = p[weatherParam];
        return {
          ts,
          value: normalizeNumericValue(v),
        };
      })
      .filter((x) => x.ts.length > 0);

    return {
      timestamps: pairs.map((x) => x.ts),
      values: pairs.map((x) => x.value),
    };
  }

  const bucket = resolution === "hourly" ? payload.hourly : payload.daily;
  if (bucket && Array.isArray(bucket.time)) {
    const rawValues = bucket[weatherParam] ?? [];
    const values = (Array.isArray(rawValues) ? rawValues : []).map((v) => normalizeNumericValue(v));
    return {
      timestamps: bucket.time,
      values,
    };
  }

  return { timestamps: [], values: [] };
}

const CompareView = () => {
  const api = useAxios();
  const toast = useToast();
  const { id: currentDeviceId } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  const headingColor = useColorModeValue("black", "gray.200");
  const boxColor = useColorModeValue("white", "gray.900");

  // ── Global settings ────────────────────────────────────────────────────────
  const [timeMode, setTimeMode] = useState<"relative" | "custom">(() => {
    const mode = searchParams.get("time_mode");
    if (mode === "relative" || mode === "custom") return mode;
    return searchParams.get("start_date") && searchParams.get("end_date") ? "custom" : "relative";
  });
  const [days, setDays] = useState<number>(() => {
    const d = Number(searchParams.get("days"));
    return DAYS_OPTIONS.includes(d) ? d : 7;
  });
  const [startDate, setStartDate] = useState<string>(
    () => searchParams.get("start_date") || dateDaysAgo(7),
  );
  const [endDate, setEndDate] = useState<string>(
    () => searchParams.get("end_date") || toDateInputValue(new Date()),
  );
  const [resolution, setResolution] = useState<"hourly" | "daily">(() =>
    searchParams.get("resolution") === "daily" ? "daily" : "hourly",
  );
  const hasInvalidCustomDateRange =
    timeMode === "custom" && startDate.length > 0 && endDate.length > 0 && startDate > endDate;

  // ── Scale states ────────────────────────────────────────────────────────────
  const [leftScale, setLeftScale] = useState<ScaleState>(() => {
    const fromParams = paramsToScale(searchParams, "left");
    // Default: sensor, current device
    if (!fromParams.sourceType && currentDeviceId) {
      return { ...defaultScaleState(), sourceType: "sensor", deviceId: currentDeviceId };
    }
    return fromParams;
  });
  const [rightScale, setRightScale] = useState<ScaleState>(() =>
    paramsToScale(searchParams, "right"),
  );

  // ── Resource lists ──────────────────────────────────────────────────────────
  const [devices, setDevices] = useState<IDevice[]>([]);
  const [fields, setFields] = useState<IFieldSummary[]>([]);
  const [listsLoading, setListsLoading] = useState(true);

  // ── Available params per device (fetched when device selected) ──────────────
  const [leftAvailableParams, setLeftAvailableParams] = useState<string[]>([]);
  const [rightAvailableParams, setRightAvailableParams] = useState<string[]>([]);

  // ── Chart data ──────────────────────────────────────────────────────────────
  const [leftData, setLeftData] = useState<ScaleData | null>(null);
  const [rightData, setRightData] = useState<ScaleData | null>(null);
  const [leftLoading, setLeftLoading] = useState(false);
  const [rightLoading, setRightLoading] = useState(false);
  const leftFetchKeyRef = useRef<string>("");
  const rightFetchKeyRef = useRef<string>("");

  // ── Fetch lists on mount ────────────────────────────────────────────────────
  useEffect(() => {
    const load = async () => {
      try {
        const [devRes, fieldRes] = await Promise.all([
          api.get("/frontend/devices/current-data/"),
          api.get("/agtech/fields/"),
        ]);
        // Backend may return array directly or a paginated { results: [] } wrapper
        const devData = devRes.data;
        setDevices(Array.isArray(devData) ? devData : (devData.results ?? []));
        const fieldData = fieldRes.data;
        setFields(Array.isArray(fieldData) ? fieldData : (fieldData.results ?? []));
      } catch (err) {
        console.error("CompareView: failed to load device/field lists", err);
        toast({
          title: "Error loading device or field list",
          status: "error",
          duration: 9000,
          isClosable: true,
        });
      } finally {
        setListsLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Sync query params whenever settings change ──────────────────────────────
  useEffect(() => {
    const next: Record<string, string> = {
      time_mode: timeMode,
      resolution,
      ...scaleToParams(leftScale, "left"),
      ...scaleToParams(rightScale, "right"),
    };
    if (timeMode === "custom") {
      if (startDate) next.start_date = startDate;
      if (endDate) next.end_date = endDate;
    } else {
      next.days = String(days);
    }
    setSearchParams(next, { replace: true });
  }, [timeMode, days, startDate, endDate, resolution, leftScale, rightScale]);

  // ── Fetch available sensor params for a device ──────────────────────────────
  const fetchDeviceParams = useCallback(
    async (deviceId: string): Promise<string[]> => {
      try {
        const aggregate = resolution === "hourly" ? "hour" : "date";
        if (timeMode === "custom") {
          if (!startDate || !endDate || startDate > endDate) return [];
        }

        const params: Record<string, string | number> = { aggregate };
        if (timeMode === "custom") {
          params.start_timestamp = toApiStartTimestamp(startDate);
          params.end_timestamp = toApiEndTimestamp(endDate);
        } else {
          params.days = days;
        }

        const res = await api.get<IPlottableDeviceData>(
          `frontend/devices/plottable-data/${deviceId}/`,
          { params },
        );
        return Object.keys(res.data).filter((k) => {
          const d = res.data[k];
          return (
            k !== "timestamp" &&
            k !== "formatted_timestamp" &&
            Array.isArray(d) &&
            (d as any[]).some((v) => v !== null)
          );
        });
      } catch {
        return [];
      }
    },
    [api, days, resolution, timeMode, startDate, endDate],
  );

  // When left device changes, refresh available params
  useEffect(() => {
    if (leftScale.sourceType === "sensor" && leftScale.deviceId) {
      fetchDeviceParams(leftScale.deviceId).then(setLeftAvailableParams);
    } else {
      setLeftAvailableParams([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leftScale.sourceType, leftScale.deviceId, days, resolution, timeMode, startDate, endDate]);

  // When right device changes, refresh available params
  useEffect(() => {
    if (rightScale.sourceType === "sensor" && rightScale.deviceId) {
      fetchDeviceParams(rightScale.deviceId).then(setRightAvailableParams);
    } else {
      setRightAvailableParams([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rightScale.sourceType, rightScale.deviceId, days, resolution, timeMode, startDate, endDate]);

  // ── Fetch chart data for a scale ────────────────────────────────────────────
  const fetchScaleData = useCallback(
    async (scale: ScaleState): Promise<ScaleData | null> => {
      if (timeMode === "custom" && (!startDate || !endDate || startDate > endDate)) {
        return null;
      }

      if (scale.sourceType === "sensor" && scale.deviceId && scale.deviceParam) {
        const aggregate = resolution === "hourly" ? "hour" : "date";
        const params: Record<string, string | number> = { aggregate };
        if (timeMode === "custom") {
          params.start_timestamp = toApiStartTimestamp(startDate);
          params.end_timestamp = toApiEndTimestamp(endDate);
        } else {
          params.days = days;
        }
        const res = await api.get<IPlottableDeviceData>(
          `frontend/devices/plottable-data/${scale.deviceId}/`,
          { params },
        );
        const raw = res.data;
        const baseTimestamps = getSensorTimestamps(raw);
        const baseValues = raw[scale.deviceParam] as (number | null)[];
        const normalizedValues = Array.isArray(baseValues)
          ? baseValues.map((v) => normalizeNumericValue(v))
          : [];
        const deviceName =
          devices.find((d) => String(d.device_id) === scale.deviceId)?.device_name ||
          `Device #${scale.deviceId}`;
        const paramLabel =
          SENSOR_PARAM_LABELS[scale.deviceParam] ?? scale.deviceParam.replace(/_/g, " ");
        return {
          timestamps: baseTimestamps,
          values: normalizedValues,
          label: `${deviceName} – ${paramLabel}`,
          isBar: paramIsBar(scale),
          canonicalFamily: getCanonicalParamFamily(scale.deviceParam),
        };
      }

      if (scale.sourceType === "field" && scale.fieldId && scale.weatherParam) {
        const fieldName =
          fields.find((f) => String(f.id) === scale.fieldId)?.field_name ||
          `Field #${scale.fieldId}`;

        const interval = resolution === "hourly" ? "hourly" : "daily";
        const params: Record<string, string | number> = { interval };
        if (timeMode === "custom") {
          params.start_date = startDate;
          params.end_date = endDate;
        } else {
          params.days = days;
        }
        const res = await api.get<IFieldWeatherHistoryResponse | IFieldWeatherHistoryPoint[]>(
          `/agtech/fields/${scale.fieldId}/weather/history`,
          { params },
        );

        const history = extractHistorySeries(res.data, resolution, scale.weatherParam);
        const filtered =
          timeMode === "custom"
            ? filterSeriesByDateRange(history.timestamps, history.values, startDate, endDate)
            : history;
        const paramLabel =
          (resolution === "hourly"
            ? FIELD_HOURLY_PARAM_LABELS[scale.weatherParam]
            : FIELD_DAILY_PARAM_LABELS[scale.weatherParam]) ?? scale.weatherParam;

        return {
          timestamps: filtered.timestamps,
          values: filtered.values,
          label: `${fieldName} – ${paramLabel}`,
          isBar: paramIsBar(scale),
          canonicalFamily: getCanonicalParamFamily(scale.weatherParam),
        };
      }

      return null;
    },
    [api, days, resolution, devices, fields, timeMode, startDate, endDate],
  );

  // ── Trigger data fetch when ready ───────────────────────────────────────────
  useEffect(() => {
    if (hasInvalidCustomDateRange) {
      setLeftData(null);
      leftFetchKeyRef.current = "";
      return;
    }
    if (isScaleReady(leftScale)) {
      const nextKey = getScaleFetchKey(leftScale, {
        timeMode,
        days,
        startDate,
        endDate,
        resolution,
      });
      if (leftFetchKeyRef.current === nextKey) return;
      leftFetchKeyRef.current = nextKey;

      setLeftLoading(true);
      fetchScaleData(leftScale)
        .then(setLeftData)
        .catch(() => setLeftData(null))
        .finally(() => setLeftLoading(false));
    } else {
      setLeftData(null);
      leftFetchKeyRef.current = "";
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leftScale, days, resolution, timeMode, startDate, endDate, hasInvalidCustomDateRange]);

  useEffect(() => {
    if (hasInvalidCustomDateRange) {
      setRightData(null);
      rightFetchKeyRef.current = "";
      return;
    }
    if (isScaleReady(rightScale)) {
      const nextKey = getScaleFetchKey(rightScale, {
        timeMode,
        days,
        startDate,
        endDate,
        resolution,
      });
      if (rightFetchKeyRef.current === nextKey) return;
      rightFetchKeyRef.current = nextKey;

      setRightLoading(true);
      fetchScaleData(rightScale)
        .then(setRightData)
        .catch(() => setRightData(null))
        .finally(() => setRightLoading(false));
    } else {
      setRightData(null);
      rightFetchKeyRef.current = "";
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rightScale, days, resolution, timeMode, startDate, endDate, hasInvalidCustomDateRange]);

  // ── Build chart ──────────────────────────────────────────────────────────────
  const isChartLoading = leftLoading || rightLoading;
  const hasData = leftData || rightData;
  const chartTimestamps = leftData?.timestamps ?? rightData?.timestamps ?? [];

  // Keep legacy behavior: use one side's labels and plot series as-is.
  const chartLabels = formatTimestamps(chartTimestamps, resolution);

  const sameCanonicalFamily =
    !!leftData &&
    !!rightData &&
    leftData.canonicalFamily.length > 0 &&
    leftData.canonicalFamily === rightData.canonicalFamily;

  const mergedAxisLabel = sameCanonicalFamily
    ? getFamilyDisplayLabel(leftData!.canonicalFamily)
    : undefined;

  // Determine if we need a mixed chart (at least one bar dataset)
  const needsBar = (leftData?.isBar || rightData?.isBar) ?? false;

  const datasets: any[] = [];
  if (leftData) {
    const base = {
      label: leftData.label,
      data:
        leftScale.sourceType === "field"
          ? alignSeriesToLabels(chartTimestamps, leftData.timestamps, leftData.values, resolution)
          : leftData.values,
      yAxisID: "y1",
      borderColor: "rgba(72, 187, 120, 0.9)",
      backgroundColor: leftData.isBar ? "rgba(72, 187, 120, 0.6)" : "rgba(72, 187, 120, 0.1)",
      pointRadius: leftData.isBar ? undefined : 0,
      fill: false,
      // In a Bar chart, non-bar datasets must declare type:'line' explicitly
      type: leftData.isBar ? "bar" : "line",
    };
    datasets.push(base);
  }
  if (rightData) {
    const base = {
      label: rightData.label,
      data:
        rightScale.sourceType === "field"
          ? alignSeriesToLabels(chartTimestamps, rightData.timestamps, rightData.values, resolution)
          : rightData.values,
      yAxisID: sameCanonicalFamily ? "y1" : "y2",
      borderColor: "rgba(66, 153, 225, 0.9)",
      backgroundColor: rightData.isBar ? "rgba(66, 153, 225, 0.6)" : "rgba(66, 153, 225, 0.1)",
      pointRadius: rightData.isBar ? undefined : 0,
      fill: false,
      type: rightData.isBar ? "bar" : "line",
    };
    datasets.push(base);
  }

  const chartOptions: any = {
    responsive: true,
    tooltips: { mode: "index", intersect: false },
    scales: {
      xAxes: [{ ticks: { maxTicksLimit: 12, maxRotation: 30 } }],
      yAxes: [
        {
          id: "y1",
          type: "linear",
          position: "left",
          display: !!leftData,
          scaleLabel: {
            display: !!leftData,
            labelString: mergedAxisLabel ?? leftData?.label ?? "",
          },
          gridLines: { drawOnChartArea: true },
        },
        {
          id: "y2",
          type: "linear",
          position: "right",
          display: !!rightData && !sameCanonicalFamily,
          scaleLabel: {
            display: !!rightData && !sameCanonicalFamily,
            labelString: rightData?.label ?? "",
          },
          gridLines: { drawOnChartArea: false },
        },
      ],
    },
  };

  return (
    <Navbar>
      <VStack mt={2} align="stretch" spacing={4} px={{ base: 2, md: 6 }} py={4}>
        {/* Header */}
        <HStack>
          <Heading as="h1" size="lg" color={headingColor}>
            <Trans>Compare</Trans>
          </Heading>
        </HStack>

        {/* Global controls */}
        <Box bg={boxColor} p={4} shadow="sm" borderRadius="md">
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
            <Box>
              <Text fontWeight="semibold" mb={2} fontSize="sm">
                <Trans>Time selection</Trans>
              </Text>
              <RadioGroup
                value={timeMode}
                onChange={(v) => setTimeMode(v as "relative" | "custom")}
              >
                <Stack direction="row" flexWrap="wrap" spacing={3}>
                  <Radio value="relative">
                    <Trans>Last N days</Trans>
                  </Radio>
                  <Radio value="custom">
                    <Trans>Custom dates</Trans>
                  </Radio>
                </Stack>
              </RadioGroup>
            </Box>

            <Box>
              <Text fontWeight="semibold" mb={2} fontSize="sm">
                <Trans>Time range</Trans>
              </Text>
              {timeMode === "relative" ? (
                <RadioGroup value={String(days)} onChange={(v) => setDays(Number(v))}>
                  <Stack direction="row" flexWrap="wrap" spacing={3}>
                    {DAYS_OPTIONS.map((d) => (
                      <Radio key={d} value={String(d)}>
                        {d} <Trans>days</Trans>
                      </Radio>
                    ))}
                  </Stack>
                </RadioGroup>
              ) : (
                <Stack direction={{ base: "column", lg: "row" }} spacing={3}>
                  <Input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    aria-label={t`Start date`}
                  />
                  <Input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    aria-label={t`End date`}
                  />
                </Stack>
              )}
              {hasInvalidCustomDateRange && (
                <Text mt={2} color="red.500" fontSize="sm">
                  <Trans>End date must be the same as or after start date.</Trans>
                </Text>
              )}
            </Box>

            <Box>
              <Text fontWeight="semibold" mb={2} fontSize="sm">
                <Trans>Resolution</Trans>
              </Text>
              <RadioGroup
                value={resolution}
                onChange={(v) => setResolution(v as "hourly" | "daily")}
              >
                <Stack direction="row">
                  <Radio value="hourly">
                    <Trans>Hourly</Trans>
                  </Radio>
                  <Radio value="daily">
                    <Trans>Daily</Trans>
                  </Radio>
                </Stack>
              </RadioGroup>
            </Box>
          </SimpleGrid>
        </Box>

        {/* Scale configurators */}
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
          <Box bg={boxColor} p={4} shadow="sm" borderRadius="md">
            <ScaleConfigurator
              label={t`Left scale`}
              accentColor="#48BB78"
              state={leftScale}
              onChange={(next) => setLeftScale((prev) => ({ ...prev, ...next }))}
              devices={devices}
              fields={fields}
              resolution={resolution}
              availableDeviceParams={leftAvailableParams}
              listsLoading={listsLoading}
            />
          </Box>

          <Box bg={boxColor} p={4} shadow="sm" borderRadius="md">
            <ScaleConfigurator
              label={t`Right scale`}
              accentColor="#4299E1"
              state={rightScale}
              onChange={(next) => setRightScale((prev) => ({ ...prev, ...next }))}
              devices={devices}
              fields={fields}
              resolution={resolution}
              availableDeviceParams={rightAvailableParams}
              listsLoading={listsLoading}
            />
          </Box>
        </SimpleGrid>

        {/* Chart */}
        <Box bg={boxColor} p={4} shadow="sm" borderRadius="md">
          <Heading as="h2" size="md" mb={4} color={headingColor}>
            <Trans>Chart</Trans>
          </Heading>

          {isChartLoading ? (
            <Skeleton height="350px" />
          ) : hasData ? (
            // Use Bar as root when any dataset is a bar (supports mixed type per dataset).
            // Non-bar datasets declare type:'line' themselves.
            needsBar ? (
              <Bar height={140} data={{ labels: chartLabels, datasets }} options={chartOptions} />
            ) : (
              <Line height={140} data={{ labels: chartLabels, datasets }} options={chartOptions} />
            )
          ) : (
            <Box textAlign="center" py={16} color="gray.500">
              <Text>
                <Trans>Configure at least one scale above to display data.</Trans>
              </Text>
            </Box>
          )}
        </Box>
      </VStack>
    </Navbar>
  );
};

export default CompareView;
